import React, { useState, useRef } from "react";
import { Trash2, Volume2, Drum, Circle, Hand, Timer, Grid3x3, Maximize2 } from "lucide-react";
import { useStudio } from "@/studio/StudioContext";
import { DRUM_VOICES } from "@/audio/drums";
import { DRUM_KIT_LIST } from "@/audio/engine";
import InfoDot from "@/studio/InfoDot";

const hits = (...idx) => {
  const a = new Array(16).fill(false);
  idx.forEach((i) => (a[i] = true));
  return a;
};
// Hi-hat lane with rolls: base 16ths, with 2/3/4 = roll bursts on chosen steps.
const rollHat = () => {
  const a = new Array(16).fill(true);
  [3, 7, 11].forEach((i) => (a[i] = 3));
  [5, 13, 15].forEach((i) => (a[i] = 2));
  return a;
};

const PRESETS = {
  House: { kick: hits(0, 4, 8, 12), clap: hits(4, 12), hat: hits(2, 6, 10, 14) },
  "Boom Bap": { kick: hits(0, 10), snare: hits(4, 12), hat: hits(0, 2, 4, 6, 8, 10, 12, 14) },
  Trap: {
    kick: hits(0, 6, 10),
    snare: hits(8),
    clap: hits(8),
    hat: rollHat(),
  },
  Techno: { kick: hits(0, 4, 8, 12), openhat: hits(2, 6, 10, 14), clap: hits(4, 12) },
  Funk: { kick: hits(0, 7, 8), snare: hits(4, 12), hat: hits(0, 2, 4, 6, 8, 10, 12, 14), tom: hits(15) },
};

export default function DrumMachine() {
  const { engine, currentStep, currentBar, currentPatStep, currentInstance, isPlaying, pushHistory, play, metronome, toggleMetronome, bpm, setBpm } = useStudio();
  const [, setLocal] = useState(0);
  const u = () => setLocal((n) => n + 1);
  const PLETTERS = ["A", "B", "C", "D", "E", "F", "G", "H"];
  const [mode, setMode] = useState("pads");
  const [recording, setRecording] = useState(false);
  const [flash, setFlash] = useState({});
  const tapTimes = useRef([]);

  const hitPad = (id) => {
    const step = engine.playPad(id);
    setFlash((f) => ({ ...f, [id]: Date.now() }));
    setTimeout(() => setFlash((f) => ({ ...f, [id]: 0 })), 130);
    if (step !== null && step !== undefined) u();
  };

  const toggleRec = () => {
    engine.ensureStarted();
    if (engine.ctx.state === "suspended") engine.ctx.resume();
    const next = !recording;
    setRecording(next);
    engine.setDrumRecording(next);
    if (next) {
      pushHistory();
      if (!isPlaying) play();
      if (!metronome) toggleMetronome();
    }
  };

  const tapTempo = () => {
    const now = performance.now();
    const arr = tapTimes.current.filter((t) => now - t < 2000);
    arr.push(now);
    tapTimes.current = arr;
    if (arr.length >= 2) {
      const intervals = [];
      for (let i = 1; i < arr.length; i++) intervals.push(arr[i] - arr[i - 1]);
      const avg = intervals.reduce((a, b) => a + b, 0) / intervals.length;
      setBpm(Math.max(40, Math.min(240, Math.round(60000 / avg))));
    }
  };

  const toggleKit = () => {
    engine.ensureStarted();
    engine.setRealDrums(!engine.realDrums);
    u();
  };

  const changeKit = async (kitId) => {
    engine.ensureStarted();
    await engine.setDrumKit(kitId);
    u();
  };

  const toggle = (voiceId, step) => {
    engine.ensureStarted();
    if (engine.ctx.state === "suspended") engine.ctx.resume();
    pushHistory();
    engine.toggleStep(voiceId, step);
    u();
    const v = DRUM_VOICES.find((x) => x.id === voiceId);
    if (engine.pattern[voiceId][step]) engine.triggerDrum(voiceId, engine.ctx.currentTime, engine.voiceGain[voiceId]);
  };

  // Right-click / long-press a step to cycle a ROLL (off -> on -> x2 -> x3 -> x4).
  const cycleRoll = (voiceId, step, e) => {
    if (e) e.preventDefault();
    engine.ensureStarted();
    if (engine.ctx.state === "suspended") engine.ctx.resume();
    pushHistory();
    engine.cycleStepRoll(voiceId, step);
    u();
    const val = engine.pattern[voiceId][step];
    if (val) engine.triggerDrum(voiceId, engine.ctx.currentTime, engine.voiceGain[voiceId]);
  };

  const loadPreset = (name) => {
    engine.ensureStarted();
    pushHistory();
    engine.setPattern(PRESETS[name]);
    u();
  };

  const clear = () => {
    pushHistory();
    engine.clearPattern();
    u();
  };

  const selectPattern = (i) => {
    engine.ensureStarted();
    engine.selectEditPattern(i);
    u();
  };
  const changeBars = (d) => {
    engine.ensureStarted();
    pushHistory();
    engine.setSongBars(engine.songBars + d * engine.patternBars);
    u();
  };
  const setPatternBars = (bars) => {
    engine.ensureStarted();
    pushHistory();
    engine.setPatternBars(bars);
    u();
  };
  const cycleChain = (slot) => {
    engine.ensureStarted();
    pushHistory();
    engine.setChainBar(slot, (engine.chain[slot] + 1) % PLETTERS.length);
    u();
  };

  return (
    <div className="flex flex-col h-full" data-testid="drum-machine">
      <div className="flex items-center gap-4 px-3 py-2 border-b border-[#262626] flex-wrap bg-[#0d0d0d]">
        <div className="flex items-center gap-1.5">
          <span className="hw-label text-[8px]">Pattern</span>
          {PLETTERS.map((L, i) => (
            <button
              key={L}
              data-testid={`pattern-${L}`}
              onClick={() => selectPattern(i)}
              title={`Edit pattern ${L}`}
              className="w-6 h-6 rounded-sm text-[10px] font-bold border transition-colors"
              style={
                engine.started && engine.currentPatternEdit === i
                  ? { borderColor: "#00e5ff", color: "#00e5ff", background: "rgba(0,229,255,0.12)" }
                  : { borderColor: "#2a2a2a", color: "#737373" }
              }
            >
              {L}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1.5">
          <span className="hw-label text-[8px]">Song</span>
          <button data-testid="bars-minus" onClick={() => changeBars(-1)} className="w-5 h-5 rounded-sm border border-[#2a2a2a] text-neutral-400 hover:text-white transition-colors">−</button>
          <span className="font-mono-data text-[10px] text-[#00e5ff] w-12 text-center">
            {engine.started ? engine.songBars : 4} bars
          </span>
          <button data-testid="bars-plus" onClick={() => changeBars(1)} className="w-5 h-5 rounded-sm border border-[#2a2a2a] text-neutral-400 hover:text-white transition-colors">+</button>
        </div>
        <div className="flex items-center gap-1.5 px-2 py-1 rounded-sm border border-[#00e5ff44] bg-[#00e5ff0a]" data-testid="pattern-length">
          <Maximize2 size={12} className="text-[#00e5ff]" />
          <span className="hw-label text-[9px] text-[#00e5ff]" title="Add more steps — build longer, fuller beats">
            Grid Size
          </span>
          {[1, 2, 4].map((b) => (
            <button
              key={b}
              data-testid={`pattern-bars-${b}`}
              onClick={() => setPatternBars(b)}
              title={`${b} bar${b > 1 ? "s" : ""} — ${b * 16} steps`}
              className="px-2.5 h-6 rounded-sm text-[10px] font-bold border transition-all"
              style={
                (engine.started ? engine.patternBars : 2) === b
                  ? { borderColor: "#00e5ff", color: "#000", background: "#00e5ff", boxShadow: "0 0 8px #00e5ff66" }
                  : { borderColor: "#2a4a4f", color: "#7fd6e0" }
              }
            >
              {b * 16}
            </button>
          ))}
          <span className="hw-label text-[7px] text-neutral-500">steps</span>
        </div>
        <div className="flex items-center gap-1 flex-1 overflow-x-auto">
          <span className="hw-label text-[8px] shrink-0">Arrangement</span>
          {(engine.started ? engine.chain : [0, 0]).map((p, slot) => {
            const playing = isPlaying && currentInstance === slot;
            const pb = engine.started ? engine.patternBars : 2;
            return (
              <button
                key={slot}
                data-testid={`chain-${slot}`}
                onClick={() => cycleChain(slot)}
                title={`Slot ${slot + 1} (bars ${slot * pb + 1}–${slot * pb + pb}) — click to change pattern`}
                className="w-7 h-7 rounded-sm text-[10px] font-bold border shrink-0 transition-colors"
                style={{
                  borderColor: playing ? "#fff" : "#2a2a2a",
                  color: "#e5e5e5",
                  background: playing ? "rgba(255,255,255,0.14)" : "#141414",
                }}
              >
                {PLETTERS[p]}
              </button>
            );
          })}
        </div>
      </div>
      <div className="flex items-center justify-between px-3 py-2 border-b border-[#262626] gap-3 flex-wrap">
        <h3 className="font-display text-sm uppercase tracking-widest text-neutral-300 flex items-center gap-1.5">
          Drum Machine
          <InfoDot section="drums" label="How the Drum Machine works" />
        </h3>
        <div className="flex rounded-sm border border-[#2a2a2a] overflow-hidden" data-testid="drum-mode-toggle">
          <button
            data-testid="drum-mode-pads"
            onClick={() => setMode("pads")}
            className="px-2.5 py-1 text-[9px] hw-label flex items-center gap-1 transition-colors"
            style={mode === "pads" ? { background: "#00e5ff", color: "#000" } : { color: "#a3a3a3" }}
          >
            <Hand size={11} /> Pads
          </button>
          <button
            data-testid="drum-mode-grid"
            onClick={() => setMode("grid")}
            className="px-2.5 py-1 text-[9px] hw-label flex items-center gap-1 transition-colors"
            style={mode === "grid" ? { background: "#00e5ff", color: "#000" } : { color: "#a3a3a3" }}
          >
            <Grid3x3 size={11} /> Grid
          </button>
        </div>
        <button
          data-testid="drum-rec-btn"
          onClick={toggleRec}
          title="Live record — hit the pads to play your beat in; hits snap to the grid"
          className="flex items-center gap-1 px-2.5 py-1 rounded-sm text-[9px] hw-label border transition-colors"
          style={
            recording
              ? { borderColor: "#ff3333", color: "#ff3333", background: "rgba(255,51,51,0.14)" }
              : { borderColor: "#2a2a2a", color: "#a3a3a3" }
          }
        >
          <Circle size={9} fill={recording ? "#ff3333" : "none"} className={recording ? "led-pulse" : ""} />
          {recording ? "Recording…" : "Live Rec"}
        </button>
        <button
          data-testid="tap-tempo-btn"
          onClick={tapTempo}
          title="Tap this button in time to set your own tempo"
          className="flex items-center gap-1 px-2.5 py-1 rounded-sm text-[9px] hw-label border border-[#2a2a2a] text-neutral-300 hover:border-[#00e5ff] hover:text-[#00e5ff] transition-colors"
        >
          <Timer size={11} /> Tap Tempo
        </button>
        <button
          data-testid="drum-kit-toggle"
          onClick={toggleKit}
          title="Switch between real recorded drums and synthesized drums"
          className="flex items-center gap-1 px-2 py-1 rounded-sm text-[9px] hw-label border transition-colors"
          style={
            engine.started && engine.realDrums
              ? { borderColor: "#ffb800", color: "#ffb800", background: "rgba(255,184,0,0.12)" }
              : { borderColor: "#2a2a2a", color: "#a3a3a3" }
          }
        >
          <Drum size={11} /> {engine.started && engine.realDrums ? "Real Kit" : "Synth Kit"}
        </button>
        {engine.started && engine.realDrums && (
          <select
            data-testid="drum-kit-select"
            value={engine.drumKit || "acoustic"}
            onChange={(e) => changeKit(e.target.value)}
            title="Choose a genre drum kit"
            className="px-2 py-1 rounded-sm text-[9px] hw-label border border-[#2a2a2a] bg-[#0a0a0a] text-[#ffb800] focus:border-[#ffb800] outline-none cursor-pointer"
          >
            {DRUM_KIT_LIST.map((k) => (
              <option key={k.id} value={k.id}>
                {k.label.replace(" ✦", "")}
              </option>
            ))}
          </select>
        )}
        <div className="flex items-center gap-1.5">
          <span className="hw-label text-[8px]">Presets</span>
          {Object.keys(PRESETS).map((name) => (
            <button
              key={name}
              data-testid={`preset-${name.replace(/\s+/g, "-").toLowerCase()}`}
              onClick={() => loadPreset(name)}
              className="px-2 py-1 rounded-sm text-[9px] hw-label border border-[#2a2a2a] hover:border-[#00e5ff] hover:text-[#00e5ff] transition-colors"
            >
              {name}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <span className="hw-label text-[8px]">Swing</span>
          <input
            data-testid="swing-slider"
            type="range"
            min={0}
            max={0.6}
            step={0.02}
            defaultValue={engine.started ? engine.swing : 0}
            onChange={(e) => {
              engine.ensureStarted();
              engine.setSwing(Number(e.target.value));
            }}
            className="w-24"
          />
          <button
            data-testid="clear-pattern-btn"
            onClick={clear}
            title="Clear the drum pattern only (the AI synth/melody is cleared from the Remix bar or Synth Keys)"
            className="flex items-center gap-1 text-[10px] hw-label hover:text-[#ff3333] transition-colors ml-2"
          >
            <Trash2 size={12} /> Clear Drums
          </button>
        </div>
      </div>

      {mode === "pads" ? (
        <div className="flex-1 overflow-auto p-4" data-testid="drum-pads">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-w-4xl mx-auto h-full content-center">
            {DRUM_VOICES.map((v) => {
              const isFlash = !!flash[v.id];
              const muted = engine.started ? engine.voiceMute[v.id] : false;
              return (
                <button
                  key={v.id}
                  data-testid={`pad-${v.id}`}
                  onPointerDown={(e) => {
                    e.preventDefault();
                    hitPad(v.id);
                  }}
                  className="relative rounded-xl border flex flex-col items-center justify-center gap-2 select-none transition-all duration-75"
                  style={{
                    minHeight: 96,
                    touchAction: "none",
                    background: isFlash ? v.color : "linear-gradient(160deg,#1a1a1a,#0c0c0c)",
                    borderColor: isFlash ? v.color : `${v.color}55`,
                    boxShadow: isFlash ? `0 0 26px ${v.color}` : `inset 0 1px 1px rgba(255,255,255,0.04)`,
                    transform: isFlash ? "scale(0.97)" : "scale(1)",
                    opacity: muted ? 0.5 : 1,
                  }}
                >
                  <Drum size={22} style={{ color: isFlash ? "#000" : v.color }} />
                  <span
                    className="font-display uppercase tracking-widest text-xs font-bold"
                    style={{ color: isFlash ? "#000" : "#d4d4d4" }}
                  >
                    {v.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      ) : (
      <div className="flex-1 overflow-auto p-3">
        <div className="flex flex-col gap-1.5 min-w-[640px]">
          {DRUM_VOICES.map((v) => {
            const muted = engine.started ? engine.voiceMute[v.id] : false;
            const steps = engine.started ? engine.patternSteps() : 16;
            const lane = engine.started ? engine.pattern[v.id] : new Array(16).fill(false);
            const activeInstance = engine.started && engine.chain[currentInstance] === engine.currentPatternEdit;
            return (
              <div key={v.id} className="flex items-center gap-2">
                <div className="w-32 shrink-0 flex items-center gap-1.5">
                  <button
                    data-testid={`voice-mute-${v.id}`}
                    onClick={() => {
                      engine.ensureStarted();
                      engine.toggleVoiceMute(v.id);
                      u();
                    }}
                    className="w-4 h-4 rounded-full shrink-0 transition-all"
                    style={{
                      background: muted ? "#2a2a2a" : v.color,
                      boxShadow: muted ? "none" : `0 0 6px ${v.color}`,
                    }}
                    title={muted ? "Unmute" : "Mute"}
                  />
                  <span className="hw-label text-[9px] w-12" style={{ opacity: muted ? 0.4 : 1 }}>
                    {v.label}
                  </span>
                  <Volume2 size={11} className="text-neutral-600 shrink-0" />
                  <input
                    data-testid={`voice-vol-${v.id}`}
                    type="range"
                    min={0}
                    max={1}
                    step={0.05}
                    defaultValue={engine.started ? engine.voiceGain[v.id] : 0.9}
                    onChange={(e) => {
                      engine.ensureStarted();
                      engine.setVoiceGain(v.id, Number(e.target.value));
                    }}
                    className="w-12"
                  />
                </div>
                <div className="flex gap-[3px] flex-1">
                  {Array.from({ length: steps }).map((_, step) => {
                    const raw = lane[step];
                    const active = !!raw;
                    const roll = typeof raw === "number" && raw >= 2 ? raw : 0;
                    const isCurrent = isPlaying && activeInstance && step === currentPatStep;
                    const isBeat = step % 4 === 0;
                    const isBar = step % 16 === 0;
                    return (
                      <button
                        key={step}
                        data-testid={`step-${v.id}-${step}`}
                        onClick={() => toggle(v.id, step)}
                        onContextMenu={(e) => cycleRoll(v.id, step, e)}
                        title="Click to toggle · right-click for a roll"
                        className="h-9 rounded-sm border transition-colors duration-75 flex-1 flex items-center justify-center"
                        style={{
                          minWidth: 14,
                          marginLeft: isBar && step !== 0 ? 6 : 0,
                          background: active ? v.color : isBeat ? "#1e1e1e" : "#131313",
                          borderColor: isCurrent ? "#fff" : active ? v.color : isBar ? "#3a3a3a" : "#242424",
                          boxShadow: active ? `0 0 8px ${v.color}88` : isCurrent ? "inset 0 0 0 1px #fff" : "none",
                          opacity: muted ? 0.35 : isCurrent && !active ? 0.7 : 1,
                        }}
                      >
                        {roll ? (
                          <span className="text-[8px] font-bold leading-none" style={{ color: "#000" }}>
                            ×{roll}
                          </span>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      )}
    </div>
  );
}
